import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/content";

const LOGO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_custom-systems-3/artifacts/9l7q7y3q_Aakuant%20Ai.PNG";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-[76px]">
        <a
          href="#top"
          className="flex items-center gap-2.5 group"
          data-testid="navbar-logo-link"
        >
          <img
            src={LOGO_URL}
            alt="Akuant Solutions"
            className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
          />
          <span className="font-outfit font-semibold text-base sm:text-lg text-white tracking-tight">
            Akuant<span className="text-[#00D2D3]">.</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-white/70 hover:text-white transition-colors duration-300 font-manrope relative group"
              data-testid={`navbar-link-${link.href.replace("#", "")}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#00D2D3] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            asChild
            className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full px-5 font-manrope font-semibold text-sm h-10 transition-all duration-300 hover:shadow-[0_0_25px_rgba(0,210,211,0.5)]"
            data-testid="navbar-form-cta"
          >
            <a href="#contato">
              Vamos construir
            </a>
          </Button>
        </div>

        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <button
              className="text-white p-2"
              data-testid="navbar-mobile-menu-trigger"
              aria-label="Abrir menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-[#0a0a0a] border-white/10 text-white"
            data-testid="navbar-mobile-menu"
          >
            <SheetTitle className="font-outfit text-white">
              Akuant Solutions
            </SheetTitle>
            <SheetDescription className="sr-only">
              Menu de navegação principal
            </SheetDescription>
            <div className="flex flex-col gap-6 mt-10">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-lg font-outfit text-white/80 hover:text-[#00D2D3] transition-colors"
                  data-testid={`navbar-mobile-link-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </a>
              ))}
              <Button
                asChild
                className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full mt-4 font-semibold"
                data-testid="navbar-mobile-form-cta"
              >
                <a href="#contato">
                  Vamos construir
                </a>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </motion.header>
  );
};

export default Navbar;
